import { createSlice, PayloadAction, createAsyncThunk, SerializedError } from '@reduxjs/toolkit'

import { createActionType } from './utils'
import { User } from '../models/client/user'
import { initialUserState } from './initialStates'
import { UserState } from './types'
import { userClient } from '../clients/UserClient'
import { clearCompanyAction } from './company'
import { clearSitesAction } from './sites'
import { clearMetadata } from './metadata'

const REDUCER_NAME = 'user'

const createUserActionType = (name: string): string => createActionType(name, REDUCER_NAME)

// TODO: handle auth better
export const loadUserAction = createAsyncThunk(
    createUserActionType('loadUserAction'),
    (token: string): Promise<User> =>
        userClient.getUser({
            token,
            userId: '01778a34-7d55-39d1-0a6c-a26f32af2c80',
        })
)

export const logoutAction = createAsyncThunk(
    createUserActionType('logout'),
    (_, thunkApi): Promise<void> => {
        thunkApi.dispatch(clearUser())
        thunkApi.dispatch(clearCompanyAction())
        thunkApi.dispatch(clearSitesAction())
        thunkApi.dispatch(clearMetadata())
        return Promise.resolve()
    }
)

const slice = createSlice({
    name: REDUCER_NAME,
    initialState: initialUserState,
    reducers: {
        addUserAction(draft, { payload }: PayloadAction<User>) {
            draft.value = payload
            draft.loading = false
            draft.isInit = true
        },
        clearUserAction(draft) {
            draft.error = null
            draft.value = null
            draft.isInit = false
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadUserAction.pending, (draft) => {
            draft.value = null
            draft.loading = true
            draft.error = null
        })
        builder.addCase(logoutAction.pending, (draft) => {
            draft.loading = true
        })
        builder.addCase(logoutAction.fulfilled, (draft) => {
            draft.loading = false
        })
        builder.addCase(loadUserAction.rejected, (draft, action) => {
            draft.error = action.error
            draft.loading = false
            draft.isInit = true
        })
        builder.addCase(loadUserAction.fulfilled, (draft, action) => {
            draft.value = action.payload
            draft.loading = false
            draft.error = null
            draft.isInit = true
        })
    },
})

export const { addUserAction: addUser, clearUserAction: clearUser } = slice.actions

interface AppState {
    user: UserState
}

export const selectUser = (state: AppState): User | null => state.user.value || null

export const selectUserUserId = (state: AppState): string | null => state.user.value?.id || null

export const selectUserToken = (state: AppState): string | null => state.user.value?.token || null

export const selectCompanyId = (state: AppState): string | null =>
    state.user.value?.companyId || null

export const selectUserLoading = (state: AppState): boolean => state.user.loading

export const selectUserInit = (state: AppState): boolean => state.user.isInit

export const selectUserError = (state: AppState): SerializedError | null => state.user.error

export const selectUserPermission = (state: AppState): User['permissions'] =>
    state.user.value?.permissions || []

export const { reducer } = slice
